import { Checkbox, Modal } from "antd";
import React, { useEffect, useState } from "react";
import LoadingOverlay from "react-loading-overlay";

import { ExclamationCircleOutlined } from "@ant-design/icons";
import { MESSAGES } from "@Constants/index";
import { QUERY_KEY } from "@Constants/query-keys";
import useDoctorArrived from "@Hooks/mutation/useDoctorArrived";
import useEndShiftSession from "@Hooks/mutation/useEndShiftSession";
import useGetTodaysQueueInfo from "@Hooks/queries/useGetTodaysQueueInfo";
import useAllActiveQueue from "@Hooks/useAllActiveQueue";
import useUpdateQueues from "@Hooks/useUpdateQueues";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router";
import { useDebounce } from "use-debounce";
import QueueHeader from "./Components/QueueHeader";
import PassedQueueDrawer from "./Components/PassedQueueDrawer";
import QueueInformation from "./Components/QueueInformation";
import QueueTable from "./Components/QueueTable";
import QueueToolbar from "./Components/QueueToolbar";
import "./Queue.css";

const { confirm } = Modal;

const Queue = () => {
  const history = useHistory();
  const queryClient = useQueryClient();

  const [keyword, setKeyword] = useState("");
  const [isShowPassedQueue, setIsShowPassedQueue] = useState(false);

  const [debounceKeyword] = useDebounce(keyword, 300);

  const todaysQueueInfo = useGetTodaysQueueInfo();
  const allActiveQueue = useAllActiveQueue({ keyword: debounceKeyword });

  const mutationEndShiftSession = useEndShiftSession();
  const mutationDoctorArrived = useDoctorArrived();
  const updateQueues = useUpdateQueues();

  useEffect(() => {
    if (
      todaysQueueInfo?.data?.message === "Tidak ada shift yang sedang berjalan" ||
      todaysQueueInfo?.data?.message === "No shifts are currently running!"
    ) {
      window.location.href = `/queues-front/show-admin`;
    }
  }, [todaysQueueInfo?.data?.message]);

  const passedQueues = allActiveQueue?.data?.data?.passed_queues;
  const passedCount =
    allActiveQueue?.data?.data?.passed_count ?? passedQueues?.length ?? 0;

  const onClickEndSession = () => {
    confirm({
      title: "Apakah Anda yakin ingin mengakhiri sesi?",
      icon: <ExclamationCircleOutlined />,
      content: "Jika Anda menekan OK, sesi ini akan segera diakhiri.",
      okButtonProps: {
        danger: true,
        size: "large",
        loading: mutationEndShiftSession.isLoading,
      },
      onOk() {
        let payload = {
          _method: "POST",
        };
        return mutationEndShiftSession.mutate(payload, {
          onSuccess: () => {
            localStorage.removeItem("shift_id");
            localStorage.removeItem("shift_token");
            return history.replace("/queues-front/show-admin");
          },
          onError: (error) => {
            return Modal.error({
              title: "Gagal Mengakhiri Shift",
              content: `${error.response?.statusText || MESSAGES.GENERAL.ERROR
                }`,
            });
          },
        });
      },
      cancelButtonProps: {
        size: "large",
      },
      cancelText: "Batal",
    });
  };

  const onClickDoctorArrived = (e) => {
    let payload = {
      _method: "PATCH",
      doctor_arrival_status: e?.target?.checked ? 1 : 0,
      shiftId: todaysQueueInfo?.data?.shift?.id,
    };
    mutationDoctorArrived.mutate(payload, {
      onSuccess: () => {
        Modal.success({
          title: "Sukses!",
          content: `Dokter ${e?.target?.checked ? "Telah" : "Tidak"
            } Di Klinik!`,
        });
        return queryClient.invalidateQueries([QUERY_KEY.GET_TODAYS_QUEUE_INFO]);
      },
      onError: (error) => {
        return Modal.error({
          title: "Gagal!",
          content: "Gagal Mengubah Status Kedatangan Dokter",
        });
      },
    });
  };
  

  return (
    <>
      <LoadingOverlay
        active={todaysQueueInfo.isLoading}
        spinner
        text="Memuat data..."
      >
        <div className="content-section">
          <div className="queue-wrapper">
            <QueueHeader
              shiftName={todaysQueueInfo.data?.shift?.name}
              onClickEndSession={onClickEndSession}
            />

            <div className="info-wrapper">
              <Checkbox
                disabled={mutationDoctorArrived.isLoading}
                checked={
                  todaysQueueInfo?.data?.shift?.doctor_arrival_status !== 0
                }
                onChange={onClickDoctorArrived}
              >
                Status Kedatangan Dokter (Ceklis Bila Dokter Ada di Klinik)
              </Checkbox>

              <QueueInformation
                currentQueue={allActiveQueue?.data?.current_queue}
                shiftData={todaysQueueInfo.data?.shift}
              />

              <QueueToolbar
                keyword={keyword}
                onChangeKeyword={(value) => setKeyword(value)}
                onClickAddQueue={() =>
                  history.push(`/add-queue/${todaysQueueInfo.data?.shift?.id}`)
                }
                passedCount={passedCount}
                onClickPassed={() => setIsShowPassedQueue(true)}
              />

              <QueueTable
                data={allActiveQueue?.queues ?? []}
                isLoading={allActiveQueue?.isLoading}
                passedQueues={passedQueues}
                passedThreshold={
                  allActiveQueue?.data?.data?.passed_queue_threshold
                }
                onClickDetail={(data) =>
                  history.push(`/active-queue/${data.id}/${data.shift.id}`)
                }
                onClickEmergency={(data) =>
                  updateQueues.onEmergencyPatient(data?.id)
                }
              />
            </div>
          </div>
        </div>
      </LoadingOverlay>

      <PassedQueueDrawer
        isShow={isShowPassedQueue}
        onClose={() => setIsShowPassedQueue(false)}
        passedQueues={passedQueues}
        passedCount={passedCount}
        threshold={allActiveQueue?.data?.data?.passed_queue_threshold}
        isLoading={allActiveQueue?.isLoading}
      />
    </>
  );
};

export default Queue;
